import React, { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/sharp-solid-svg-icons/faArrowLeft';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import AddressValidation from './address-validation';
import style from './create-label-content.module.less';
import { handleValidatedAddress } from './create-label-helpers';
import { RateCalcFulfillmentConfiguration } from './fulfillment-configuration';
import { AddressFactory } from '../../address';
import { logPasteAddressUsage as logPasteAddressUsageAction } from '../../address/actions';
import * as AddressApi from '../../address/address.api';
import { CREATE_LABEL_CONTENT_RETURN_FROM_ADDRESS_TYPE, SHIP_TO_ADDRESS_TYPE } from '../../address/prop-types/address-type';
import { PickupInformation } from '../../components/orders-drawer/sections/pickup-information';
import ValidateAddressBox from '../../components/validate-address-box';
import { emptyArray } from 'ui-components/default-props/empty';
import { useFulfillmentPlannerContext } from '../../fulfillment-planner/context';
import FulfillmentPlannerContextDrivenOtherShippingOptions from '../../fulfillment-planner/other-shipping-options';
import * as CustomerActions from '../../store/customers/autocomplete/customers.autocomplete.actions';
import Global from '../../store/global-selectors';
import * as RateBrowserActions from '../../store/rate-browser/rate-browser.actions';
import CustomsWidgetContainer from '../customs-declarations/container';

const requiredFields = ['name', 'line1', 'city', 'state', 'postalCode'];

const getAddressType = (isReturn) => {
  return isReturn ? CREATE_LABEL_CONTENT_RETURN_FROM_ADDRESS_TYPE : SHIP_TO_ADDRESS_TYPE;
};

/**
 * Create label content component with address entry, validation, and shipping configuration
 * @param {object} props - Component props
 * @param {string} props.fulfillmentPlanId - ID for the fulfillment plan
 * @param {boolean} [props.shouldShowBackButton] - Whether to show the back button (used in rate browser)
 * @returns {React.ReactElement} Create label content component
 */
export const CreateLabelContent = ({ fulfillmentPlanId, shouldShowBackButton = false }) => {
  const dispatch = useDispatch();
  const { emailAddress, isReturn, onIsReturnChanged, onShipToChange, onShipToEmailChange, shipTo } = useFulfillmentPlannerContext();

  // Redux state
  const autoCompleteCustomers = useSelector(Global.selectCustomersAutocompleteSearchResults) ?? emptyArray;

  // Redux actions
  const onCustomerSearchChange = useCallback(
    (searchTerm) => {
      dispatch(CustomerActions.onCustomerSearchChange(searchTerm));
    },
    [dispatch],
  );

  const clearCustomerResults = useCallback(() => {
    dispatch(CustomerActions.clearCustomerSearch());
  }, [dispatch]);

  const logPasteAddressUsage = useCallback(() => {
    dispatch(logPasteAddressUsageAction());
  }, [dispatch]);

  const goBackToRates = useCallback(() => {
    if (shouldShowBackButton) {
      dispatch(RateBrowserActions.backToRates());
    }
  }, [dispatch, shouldShowBackButton]);

  const onAddressChanged = useCallback(
    (newAddress) => {
      onShipToChange(newAddress);
    },
    [onShipToChange],
  );

  const [localAddress, setLocalAddress] = useAsyncSafeState(shipTo);
  const [suggestedAddress, setSuggestedAddress] = useAsyncSafeState(null);

  const clearCustomerResultsCallback = useCallback(() => {
    // this a hack to prevent the results from clearing
    // before the selection propagates through.
    // TODO: not do this
    setTimeout(() => {
      clearCustomerResults();
    }, 200);
  }, [clearCustomerResults]);

  const isFormUpdatedRef = useRef(false);

  const onAddressFieldBlurred = useCallback(() => {
    if (isFormUpdatedRef.current) {
      isFormUpdatedRef.current = false;
      clearCustomerResultsCallback();
      onAddressChanged(localAddress);
    }
  }, [clearCustomerResultsCallback, localAddress, onAddressChanged]);

  const onLocalAddressChanged = useCallback(
    (path, value) => {
      isFormUpdatedRef.current = true;
      if (path === 'email') {
        onShipToEmailChange(value);
        return;
      }
      if (path === 'name') {
        onCustomerSearchChange(value);
      }

      setLocalAddress((state) => {
        if (state[path] === value) {
          return state;
        }
        return {
          ...state,
          [path]: value,
          verificationStatus: undefined,
        };
      });
    },
    [onCustomerSearchChange, setLocalAddress, onShipToEmailChange],
  );

  const onCustomerSelected = useCallback(
    (customerIndex) => {
      const selectedCustomer = autoCompleteCustomers[customerIndex];
      if (selectedCustomer) {
        const newAddress = {
          ...localAddress,
          name: selectedCustomer.name,
          company: selectedCustomer.company,
          line1: selectedCustomer.line1,
          line2: selectedCustomer.line2,
          line3: selectedCustomer.line3,
          city: selectedCustomer.city,
          state: selectedCustomer.state,
          postalCode: selectedCustomer.postalCode,
          countryCode: selectedCustomer.countryCode,
          phone: selectedCustomer.phone,
          verificationStatus: selectedCustomer.verificationStatus,
          verificationMessage: selectedCustomer.verificationMessage,
          verificationUtc: selectedCustomer.verificationUtc,
        };
        clearCustomerResultsCallback();
        setLocalAddress(newAddress);
        onShipToChange(newAddress);
        onShipToEmailChange(selectedCustomer.email);
      }
    },
    [autoCompleteCustomers, localAddress, clearCustomerResultsCallback, setLocalAddress, onShipToEmailChange, onShipToChange],
  );

  const pasteAddressRequestRef = useRef(null);
  useEffect(() => {
    return () => {
      if (pasteAddressRequestRef?.current) {
        pasteAddressRequestRef?.current?.cancel();
      }
    };
  }, []);
  const pasteAddressRequest = useCallback(
    (value) => {
      logPasteAddressUsage();
      pasteAddressRequestRef.current = debounce(() => {
        AddressApi.getParsedAddressPromise(value)
          .then((parsedAddress) => {
            setLocalAddress(parsedAddress);
            onAddressChanged(parsedAddress);
          })
          .catch(noop);
      }, 500);
      pasteAddressRequestRef.current();
    },
    [onAddressChanged, logPasteAddressUsage, setLocalAddress],
  );

  const validateAddress = useCallback(
    (addressToValidate) => {
      AddressApi.validateAddressPromise(addressToValidate)
        .then((responseAddresses) => {
          handleValidatedAddress(responseAddresses[0], setLocalAddress, setSuggestedAddress);
        })
        .catch(noop);
    },
    [setLocalAddress, setSuggestedAddress],
  );

  const onUseValidatedAddress = useCallback(() => {
    const updatedAddress = {
      ...shipTo,
      ...suggestedAddress,
      verificationStatus: 'VER',
    };
    setLocalAddress(updatedAddress);
    setSuggestedAddress(null);
    onAddressChanged(updatedAddress);
  }, [shipTo, suggestedAddress, onAddressChanged, setLocalAddress, setSuggestedAddress]);
  return (
    <div className={style['create-label-content']}>
      <div className={style.header}>
        {shouldShowBackButton ? (
          <Button className={style['back-to-rates-button']} onClick={goBackToRates} theme='tertiary'>
            <FontAwesomeIcon icon={faArrowLeft} />
            <FormattedMessage defaultMessage='Back To Rates' description='createLabelContent.backToRates' id='vc5m0u' />
          </Button>
        ) : null}
        <FormattedMessage
          defaultMessage='Reminder this shipment will not be associated with an order.'
          description='createLabelContent.orderReminder'
          id='rk+/zL'
        />
      </div>
      <div className={style.body}>
        <div className={style['left-section']}>
          <Checkbox
            checked={isReturn}
            className={style['create-return-label-checkbox']}
            label={
              <FormattedMessage defaultMessage='Create a Return Label' description='createLabelContent.createReturnLabel' id='rQxH5z' />
            }
            onChange={onIsReturnChanged}
          />
          <AddressFactory
            address={localAddress}
            addressType={getAddressType(isReturn)}
            autoCompleteRecipientName
            customers={autoCompleteCustomers}
            email={emailAddress}
            isControlled
            onBlur={onAddressFieldBlurred}
            onChange={onLocalAddressChanged}
            onCustomerSelected={onCustomerSelected}
            onSave={noop}
            pasteAddressRequest={pasteAddressRequest}
            requiredFields={requiredFields}
          />
          <div className={style['email-field-helper-message']}>
            <FormattedMessage
              defaultMessage='Entering an email address will create a customer record'
              description='A message below the email field informing the user that entering a new email will create a customer'
              id='pB4/Rs'
            />
          </div>
          <div className={style['validate-address-button-wrapper']}>
            <AddressValidation shipToAddress={shipTo} validateAddress={validateAddress} />
          </div>
          {suggestedAddress ? (
            <ValidateAddressBox onUseValidatedAddress={onUseValidatedAddress} validatedAddress={suggestedAddress} />
          ) : null}
        </div>
        <div className={style['right-section']}>
          <div className={style['shipping-section']}>
            <div className={style['fulfillment-planner-wrapper']}>
              <RateCalcFulfillmentConfiguration
                fulfillmentPlanId={fulfillmentPlanId}
                returnMetadata={
                  isReturn && {
                    emailAddress,
                  }
                }
              />
            </div>
            {!isReturn && (
              <div className={style['other-and-customs-wrapper']}>
                <div className={style['customs-declarations']}>
                  <CustomsWidgetContainer fulfillmentPlanId={fulfillmentPlanId} />
                </div>
                <FulfillmentPlannerContextDrivenOtherShippingOptions />
                <PickupInformation currentFulfillmentPlanId={fulfillmentPlanId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CreateLabelContent.displayName = 'CreateLabelContent';

export default CreateLabelContent;
